import React, { RefObject } from 'react'
import FlameGraph from './react-flame-graph/DataConverter'
import { TimelineSpan } from '../domain/TimelineSpan'
import {
    DifferentialFlamegraphNode,
    findFlamegraphNode,
    FlamegraphNode,
    getTraceSpanCategoryFromFlamegraphNode,
    isDifferentialFlamegraphNode,
    differentialFlamegraphMissingStats,
    TraceSpanCategory,
    differentialFlamegraphPercentageChange,
} from '../domain/Trace'
import Router from '../router'
import SpanService from './spanService'
import Event from './timeline/event'
import { FrameSummary } from './flamegraph/FrameSummary'
import { Box, BoxHeader, BoxTitle } from '../ui/box'
import { RawData } from './react-flame-graph/types'
import { DiffDetails } from './flamegraph/DiffDetails'
import { TraceDetails } from './tracedetails/TraceDetails'
import { TraceDetailsTitle } from './tracedetails/TraceDetailsTitle'
import TraceDetailsSpan from './tracedetails/TraceDetailsSpan'
import { LayerReport, renderTraceDiffTranslationEarlierLater, TraceDiffTranslation } from '../domain/TraceSummary'
import { Col } from '../ui/grid'
import LabeledRect from './react-flame-graph/LabeledRect'
import { Alert } from '../ui/alert'
import LargeNumber from '../helper/large-number'
import Milliseconds from '../helper/milliseconds'
import { TraceDetailsLayerReport } from './tracedetails/TraceDetailsLayerReport'

interface ConstantProps {
    categories: { [category: string]: TraceSpanCategory }
    spans: TimelineSpan[]
    params?: { [name: string]: string }
    router: Router
    route: string
    publicVersion: boolean
    isAggregatedTrace: boolean
}

type DynamicProps =
    | {
          differentialMode: true
          data: DifferentialFlamegraphNode
          layers: { base: LayerReport; target: LayerReport }
          switchComparisonOrder: () => void
          traceDiffTranslation: TraceDiffTranslation
      }
    | {
          differentialMode: false
          data: FlamegraphNode
          layerReport: LayerReport
      }

type Props = ConstantProps & DynamicProps

interface State {
    mode: DisplayMode
    resized: number
    chartToTop: number
}

interface SelectedIds {
    selectedSpanId: string
    selectedEventCs?: number
    selectedNodeId: string
    zoomedInNodeId: string
}

type DisplayMode = 'walltime' | 'memory'

export class FlamegraphComponent extends React.PureComponent<Props, State> {
    public static defaultProps = {
        route: 'XhprofProfilerBundle.Profile.flamegraph',
        publicVersion: false,
        differentialMode: false,
    }

    private spanService: SpanService
    private divRef: RefObject<any>
    private svgRef
    private readonly DEFAULT_WIDTH = 600

    constructor(props) {
        super(props)
        this.spanService = new SpanService()
        this.state = {
            mode: 'walltime',
            resized: 0,
            chartToTop: 170,
        }

        window.addEventListener('resize', this.triggerUpdateOnResize)

        this.divRef = React.createRef()
        this.svgRef = React.createRef()
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.triggerUpdateOnResize)
    }

    componentDidMount() {
        this.triggerUpdateOnResize()
    }

    public render() {
        const { params, route, categories, publicVersion, data, isAggregatedTrace, differentialMode } = this.props

        const originalSpans = this.spanService.sort(this.props.spans)

        const { selectedSpanId, selectedEventCs, selectedNodeId, zoomedInNodeId } = this.extractSelectedIds(params, data.id)

        const selectedSpan = originalSpans.find(span => span.id === selectedSpanId) || null
        const selectedEvent =
            selectedSpan && selectedSpan.e && selectedEventCs ? selectedSpan.e.find(event => event.cs === selectedEventCs) : null

        const selectedNode = findFlamegraphNode(data, selectedNodeId)

        const showZoomReset = zoomedInNodeId !== data.id

        const width = this.divRef.current ? jQuery(this.divRef.current).width() : this.DEFAULT_WIDTH

        const flamegraph = differentialMode
            ? this.computeFlamegraphFromDifferentialFlamegraph(data, this.state.mode)
            : this.computeFlamegraph(data, this.state.mode)

        let diffStats: null | { ct: number; wt: number } = null
        if (differentialMode) {
            diffStats = differentialFlamegraphMissingStats(data)
        }

        return (
            <div className="row flamegraph">
                <div className="col-lg-8 col-md-12">
                    <Box className="timeline-chart">
                        <div className="box-body" ref={this.divRef}>
                            {differentialMode && diffStats && diffStats.ct > 0 ? (
                                <>
                                    <Alert level={'info'}>
                                        There are <LargeNumber value={diffStats.ct} /> calls for <Milliseconds value={diffStats.wt} /> not
                                        shown, that are present only in{' '}
                                        {renderTraceDiffTranslationEarlierLater(this.props.traceDiffTranslation.to)} of both traces.{' '}
                                        <a onClick={this.props.switchComparisonOrder}>
                                            <i className="fa fa-exchange" /> Switch the comparison order
                                        </a>{' '}
                                        to see them.
                                    </Alert>
                                    <div className="tw-mb-16"></div>
                                </>
                            ) : null}
                            <div className="row">
                                <Col>
                                    <div className="flamegraph-header">
                                        <div className="btn-group">
                                            <button
                                                type="button"
                                                className={
                                                    this.state.mode === 'memory'
                                                        ? 'btn btn-xs btn-default'
                                                        : 'btn btn-xs btn-default active'
                                                }
                                                onClick={() => this.setState({ mode: 'walltime' })}
                                            >
                                                Time
                                            </button>
                                            <button
                                                type="button"
                                                className={
                                                    this.state.mode === 'memory'
                                                        ? 'btn btn-xs btn-default active'
                                                        : 'btn btn-xs btn-default'
                                                }
                                                onClick={() => this.setState({ mode: 'memory' })}
                                            >
                                                Memory
                                            </button>
                                        </div>
                                        {showZoomReset ? (
                                            <button type="button" className="tw-ml-16 btn btn-xs" onClick={() => this.zoomOnSpan(data.id)}>
                                                <i className="fa fa-search-minus"></i> reset zoom
                                            </button>
                                        ) : null}
                                        {differentialMode ? (
                                            <div className="pull-right">
                                                <svg width={600} height={22}>
                                                    {[-100, -75, -50, -25, 0, 25, 50, 100, 200, 300].map((value: number, index: number) => (
                                                        <LabeledRect
                                                            key={index}
                                                            x={index * 60}
                                                            y={0}
                                                            label={(value > 0 ? '+' : '') + `${value}%`}
                                                            backgroundColor={this.diffBackgroundColor(value, 100)}
                                                            color={this.diffColor(value, 100)}
                                                            disableDefaultTooltips
                                                            height={22}
                                                            width={60}
                                                            onClick={() => {}}
                                                            onMouseEnter={() => {}}
                                                            onMouseMove={() => {}}
                                                            onMouseLeave={() => {}}
                                                            isHighlighted={false}
                                                            percentage={0}
                                                        />
                                                    ))}
                                                </svg>
                                            </div>
                                        ) : null}
                                    </div>
                                </Col>
                            </div>
                            <div className="row tw-mt-16 text-center" ref={svg => (this.svgRef = svg)}>
                                <Col>
                                    <div
                                        className="timeline-chart__container"
                                        style={{
                                            maxHeight: `calc(100vh - ${Math.max(0, Math.round(this.state.chartToTop))}px)`,
                                        }}
                                    >
                                        <FlameGraph
                                            width={width}
                                            height={600}
                                            data={flamegraph}
                                            onChange={(node, uid) =>
                                                selectedNodeId === node.source.id
                                                    ? this.zoomOnSpan(node.source.id)
                                                    : this.selectSpan(node.source.id)
                                            }
                                            startNode={zoomedInNodeId}
                                            highlightNode={selectedNodeId}
                                        />
                                    </div>
                                </Col>
                            </div>
                        </div>
                    </Box>
                </div>
                <div className="col-lg-4 col-md-12">
                    {selectedNode ? (
                        <TraceDetails>
                            <TraceDetailsTitle
                                isRoot={selectedNodeId === data.id}
                                category={getTraceSpanCategoryFromFlamegraphNode(selectedNode)}
                                name={selectedNode.name}
                                namespace={selectedSpan?.annotations?.namespace || undefined}
                            />
                            {selectedNodeId === data.id && !differentialMode && this.props.layerReport ? (
                                <TraceDetailsLayerReport layerReport={this.props.layerReport} wt={this.props.spans[0].wt} />
                            ) : null}
                            {differentialMode && isDifferentialFlamegraphNode(selectedNode) ? (
                                <>
                                    <DiffDetails
                                        node={selectedNode}
                                        layers={selectedNode.id === data.id ? this.props.layers : undefined}
                                        traceDiffTranslation={this.props.traceDiffTranslation}
                                    />
                                    <hr className="tw-m-0" />
                                </>
                            ) : null}
                            {selectedNode?.stats && (selectedNode.stats.ct > 1 || !selectedSpan) ? (
                                <>
                                    <FrameSummary
                                        node={selectedNode}
                                        traceTotals={{ ...data.values }}
                                        isAggregatedTrace={isAggregatedTrace}
                                    />
                                    <hr className="tw-m-0" />
                                </>
                            ) : null}
                            {selectedSpan && selectedNode?.stats?.ct > 1 ? (
                                <BoxHeader>
                                    <BoxTitle className="tw-mt-0" muted>
                                        Example Span
                                    </BoxTitle>
                                </BoxHeader>
                            ) : null}
                            {selectedSpan ? (
                                <TraceDetailsSpan
                                    wt={data.values.wt}
                                    mc={data.values.mc}
                                    span={selectedSpan}
                                    parents={this.spanService.getParents(originalSpans, selectedSpan)}
                                    categories={categories}
                                    route={route}
                                    showSettingsLink={!publicVersion}
                                    bottleneckRoute={
                                        publicVersion
                                            ? 'XhprofCoreBundle.Public.sharedTrace.bottlenecks'
                                            : 'XhprofProfilerBundle.Profile.bottlenecksReport'
                                    }
                                />
                            ) : null}
                            {selectedEvent ? (
                                <>
                                    <hr className="tw-m-0" />
                                    <Event event={selectedEvent} categories={this.props.categories} />
                                </>
                            ) : null}
                        </TraceDetails>
                    ) : null}
                </div>
            </div>
        )
    }

    private selectSpan(spanId) {
        this.setQueryParameter({ span: spanId })
    }

    private zoomOnSpan(spanId) {
        this.setQueryParameter({ span: spanId, spanZoom: spanId })
    }

    private setQueryParameter(values) {
        const params = { ...this.props.params, ...values }
        const pathTo = this.props.route
        this.props.router.goTo(pathTo, params)
    }

    private computeFlamegraph = (flamegraph: FlamegraphNode, mode: DisplayMode): RawData => {
        const { cat, customType, values, children, ...rawData } = flamegraph

        const value = mode === 'walltime' ? values.wt : values.mc
        const classForFrame = 'category-' + getTraceSpanCategoryFromFlamegraphNode(flamegraph).className
        const color = getTraceSpanCategoryFromFlamegraphNode(flamegraph).fgColor || 'white'

        return {
            ...rawData,
            value,
            classForFrame,
            color,
            children: children.map(child => this.computeFlamegraph(child, mode)),
        }
    }

    private computeFlamegraphFromDifferentialFlamegraph = (flamegraph: DifferentialFlamegraphNode, mode: DisplayMode): RawData => {
        const { cat, customType, values, children, delta, ...rawData } = flamegraph

        const value = mode === 'walltime' ? values.wt : values.mc
        const deltaValue = mode === 'walltime' ? flamegraph.delta.wt : flamegraph.delta.mc

        const color = this.diffColor(deltaValue, value)
        const backgroundColor = this.diffBackgroundColor(deltaValue, value)
        rawData.name += ` [${this.deltaLabel(deltaValue, value, mode)}]`

        return {
            ...rawData,
            value,
            color,
            backgroundColor,
            children: children.map(child => this.computeFlamegraphFromDifferentialFlamegraph(child, mode)),
        }
    }

    private diffBackgroundColor(delta: number, value: number): string {
        const red = (intensity: number) => `rgba(205,15,44,${intensity / 100})`
        const blue = (intensity: number) => `rgba(9,0,255,${intensity / 100})`

        const percentageChange = Math.abs((delta / value) * 100)
        const percentageCapped = delta > 0 ? Math.min(100, percentageChange / 3) : Math.min(100, percentageChange)

        if (percentageChange < 30) {
            return '#eee'
        }

        return delta > 0 ? red(percentageCapped) : blue(percentageCapped)
    }

    private diffColor(delta: number, value: number): string {
        const percentageChange = Math.abs((delta / value) * 100) / (delta > 0 ? 3 : 1)

        return percentageChange > 75 ? 'white' : 'black'
    }

    private extractSelectedIds = (params, fallback: string): SelectedIds => {
        if (!params || !params.span) {
            return { selectedSpanId: fallback, selectedNodeId: fallback, zoomedInNodeId: fallback }
        }

        const buildEventId = (spanId: string, eventCs: number): string => `${spanId}[event:${eventCs}]`

        const zoomedInNodeId = params.spanZoom || fallback
        const selectedSpanId = params.span

        const regex = /^([a-z0-9]+)\[event:(\d+)\]$/i

        if (regex.test(selectedSpanId)) {
            const match = regex.exec(selectedSpanId)
            if (match) {
                return {
                    selectedSpanId: match[1],
                    selectedEventCs: +match[2],
                    selectedNodeId: buildEventId(match[1], +match[2]),
                    zoomedInNodeId,
                }
            }
        } else if (params && 'e' in params) {
            return {
                selectedSpanId,
                selectedEventCs: +params.e,
                selectedNodeId: buildEventId(selectedSpanId, +params.e),
                zoomedInNodeId,
            }
        }

        return { selectedSpanId, selectedNodeId: selectedSpanId, zoomedInNodeId }
    }

    private triggerUpdateOnResize = () => {
        this.setState({
            resized: this.state.resized + 1,
            chartToTop: 300,
        })
    }

    private deltaLabel(delta: number, value: number, mode: DisplayMode): string {
        if (!this.props.differentialMode) {
            throw Error('deltas are only present in differential flamegraphs')
        }

        const { traceDiffTranslation } = this.props

        const percentageChange = differentialFlamegraphPercentageChange(value, delta)
        const percentageChangeFormatted = percentageChange.toLocaleString('en-US', {
            signDisplay: 'never',
            style: 'percent',
        })

        if (Math.abs(percentageChange) <= 0.2) {
            return 'unchanged'
        }

        if (Math.abs(value - delta) < 0.01) {
            return `only ${traceDiffTranslation.to.toLowerCase()}`
        }

        if (mode === 'walltime') {
            return `${percentageChangeFormatted} ${delta > 0 ? 'slower' : 'faster'} ${traceDiffTranslation.to.toLowerCase()}`
        }

        return `${percentageChangeFormatted} ${delta > 0 ? 'higher' : 'lower'} ${traceDiffTranslation.to.toLowerCase()}`
    }
}
